import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Flex,
  PrimaryButton,
  SafeAreaBottomSpacing,
  Typography,
} from '@banhala/ui-kit';
import { useGoodsDetail } from '../../queries/useGoodsDetail';
import {
  bridge,
  convertToDayJs,
  getToday,
  isNil,
  isNotNil,
  isWeb,
} from 'shared/src/utils';
import { NudgeVerticalCarousel } from './components/NudgeVerticalCarousel';
import { LikeButton } from './components/LikeButton';
import { GiftButton } from './components/GiftButton';
import { TopGradientShadow } from './components/TopGradientShadow';
import { TimeSaleBanner } from './components/TimeSaleBanner';
import { ImageBadgeFreeDelivery } from './components/ImageBadgeFreeDelivery';
import { useIsOnSale } from '../../hooks/useIsOnSale';
import { useGoodsBasic } from '../../queries/useGoodsBasic';
import { useSelectOptionBottomSheet } from '../SelectOptionBottomSheet';
import { usePostGoodsActionBottomSheet } from '../PostGoodsActionBottomSheet';
import { BottomDockedButtonSkeleton } from './BottomDockedButton.skeleton';
import type { GoodsOption } from 'shared/src/api';
import { useRegisterBottomDockedHeight } from 'shared/src/hooks/useBottomDockedHeight';
import { useCartAddRecommend } from './queries/useCartAddRecommend';
import Toast from 'react-native-toast-message';
import { useOpenDeeplink } from 'shared/src/hooks';
import { useBottomDockedTimeSaleBanner } from './hooks/useBottomDockedTimeSaleBanner';

const BOTTOM_DOCKED_HEIGHT = 56 + 12 * 2;

export const BottomDockedButton: React.FC = () => {
  useRegisterBottomDockedHeight(BOTTOM_DOCKED_HEIGHT);
  const { data: goodsDetail } = useGoodsDetail();
  const { data: goodsBasic } = useGoodsBasic();
  // TimeSaleBanner와 동일 조건 — 72h 초과/만료 시 null → 넛지 캐러셀 (빈 슬롯 방지)
  const timeSaleBanner = useBottomDockedTimeSaleBanner();
  const isOnSaleNow = useIsOnSale();
  const [isShowOnce, setIsShowOnce] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<GoodsOption[]>([]);

  const { show: showSelectOptionBottomSheet } = useSelectOptionBottomSheet();
  const { show: showPostGoodsActionBottomSheet } =
    usePostGoodsActionBottomSheet();
  const { data: cartRecommend, refetch: refetchCartRecommend } =
    useCartAddRecommend();
  const cartRecommendRef = useRef(cartRecommend);
  cartRecommendRef.current = cartRecommend;
  const cartRecommendModalShownRef = useRef(false);
  const { openDeeplink } = useOpenDeeplink();

  const isDisabled = useMemo(() => {
    if (goodsBasic?.sale_type === 'ON_SALE') return false;
    if (goodsBasic?.sale_type === 'SALE_WAIT') return false;

    if (goodsBasic?.sale_type === 'SOLD_OUT') return true;
    if (goodsBasic?.sale_type === 'STOP_SALE') return true;

    return false;
  }, [goodsBasic?.sale_type]);

  const isGiftPurchase = useMemo(() => {
    if (isNotNil(goodsBasic)) {
      if (isOnSaleNow) {
        return !goodsBasic.is_overseas_delivery;
      }
    }
    return false;
  }, [goodsBasic, isOnSaleNow]);

  const buttonText = useMemo(() => {
    if (goodsBasic?.sale_type === 'SOLD_OUT') return '품절';
    if (goodsBasic?.sale_type === 'STOP_SALE')
      return '판매가 종료된 상품입니다';
    if (goodsBasic?.sale_type === 'SALE_WAIT') {
      if (isNotNil(goodsBasic?.sale_start_at)) {
        const saleStartTime = convertToDayJs(goodsBasic?.sale_start_at);

        const now = getToday();
        const today = saleStartTime
          .startOf('day')
          .diff(now.startOf('day'), 'days');
        const diffDays = Math.abs(today);
        if (diffDays > 1) {
          return `${saleStartTime.format('MM/DD HH:mm')} 오픈`;
        }

        if (diffDays === 1) {
          return `내일 ${saleStartTime.format('HH:mm')} 오픈`;
        }
        return `오늘 ${saleStartTime.format('HH:mm')} 오픈`;
      }
      return '품절';
    }
    return '구매하기';
  }, [goodsBasic?.sale_start_at, goodsBasic?.sale_type]);

  const onPressRestockNoti = useCallback(() => {
    // bridge.toast('재입고 알림을 작성해주세요');
    setIsShowOnce(true);
    showSelectOptionBottomSheet({
      mode: 'purchase',
      isShowOnce,
      selectedOptions: [],
      updateSelectedOption: setSelectedOptions,
    });
  }, [showSelectOptionBottomSheet, isShowOnce]);

  const onPressReserveInquiry = useCallback(() => {
    bridge.toast('예약 문의하기를 작성해주세요');
  }, []);

  const onPressGift = useCallback(() => {
    if (isDisabled) return;
    setIsShowOnce(true);
    showSelectOptionBottomSheet({
      mode: 'gift',
      isShowOnce,
      selectedOptions: [],
      updateSelectedOption: setSelectedOptions,
    });
  }, [isDisabled, showSelectOptionBottomSheet, isShowOnce]);

  // 담기 성공 시점에 호출 — 추천 캐러셀이 있으면 추천 시트, 없으면 토스트.
  // 옵션시트가 완전히 닫힌 뒤(await 결과)가 아니라 담기 성공 즉시 호출되므로,
  // 옵션시트 닫힘 애니(250ms)와 이 시트 열림 애니(300ms)가 직렬이 아니라 병렬로 겹친다
  // (네이티브의 picker dismiss ↔ 추천 modal show 오버랩과 정합, 체감 지연 단축).
  const showCartRecommendOrToast = useCallback(async () => {
    // PDP 진입 직후 빠른 사용자 케이스에서 query가 아직 미완료일 수 있어,
    // 캐시된 데이터가 없을 때만 refetch로 완료 대기.
    const recommendData =
      cartRecommendRef.current ??
      (await refetchCartRecommend()
        .then(r => r.data)
        .catch(() => undefined));
    const hasRecommendCarousels = (recommendData?.carousels.length ?? 0) > 0;
    const landingDeeplink = isWeb
      ? `ably://webview?url=https://m.a-bly.com/cart`
      : `ably://webview?title_bar=false&url=cart%3Ffrom%3DRECOMMENDEDMODAL`;

    if (hasRecommendCarousels && !cartRecommendModalShownRef.current) {
      cartRecommendModalShownRef.current = true;
      showPostGoodsActionBottomSheet({
        mode: 'cart',
        title: {
          highlight: '장바구니',
          text: '에 상품을 담았어요',
        },
        image: goodsBasic?.cover_images[0] ?? '',
        action: {
          text: '바로가기',
          deeplink: landingDeeplink,
        },
      });
    } else {
      Toast.show({
        type: 'success',
        position: 'bottom',
        props: {
          customText: (
            <Flex.Row
              padding={{ vertical: 12 }}
              width="100%"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography type="body2" color="content_always_white">
                장바구니에 상품을 담았어요
              </Typography>
              <Flex.Column
                onPress={() => {
                  openDeeplink(landingDeeplink);
                }}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Typography color="content_blue_primary">바로가기</Typography>
              </Flex.Column>
            </Flex.Row>
          ),
        },
      });
    }
  }, [
    refetchCartRecommend,
    showPostGoodsActionBottomSheet,
    goodsBasic?.cover_images,
    openDeeplink,
  ]);

  const onPressPurchase = useCallback(async () => {
    if (isDisabled) return;

    setIsShowOnce(true);
    await showSelectOptionBottomSheet({
      mode: 'purchase',
      isShowOnce,
      selectedOptions,
      updateSelectedOption: setSelectedOptions,
      onAddToCartSuccess: showCartRecommendOrToast,
    });
  }, [
    isDisabled,
    showSelectOptionBottomSheet,
    isShowOnce,
    selectedOptions,
    showCartRecommendOrToast,
  ]);

  if (isNil(goodsBasic)) {
    return <BottomDockedButtonSkeleton />;
  }

  return (
    <Flex>
      {/*<Flex shadowStep={'medium'} />*/}
      <TopGradientShadow />
      {isNotNil(timeSaleBanner) ? (
        <TimeSaleBanner
          title={timeSaleBanner.title}
          finishedAt={timeSaleBanner.finishedAt}
        />
      ) : (
        <NudgeVerticalCarousel />
      )}
      {isNotNil(goodsDetail) &&
      goodsDetail.goods.order_entry_type === 'INQUIRY' ? (
        <Flex.Row
          alignItems={'center'}
          padding={{ horizontal: 12, vertical: 12 }}
        >
          <LikeButton
            initialIsLiked={isNotNil(goodsDetail.goods.like)}
            likesCount={goodsDetail.goods.score.likes_count}
          />
          <Flex.Row flex={1} gap={8}>
            <Flex flex={1}>
              <PrimaryButton variant={'fill'} onPress={onPressReserveInquiry}>
                예약 문의하기
              </PrimaryButton>
            </Flex>
          </Flex.Row>
        </Flex.Row>
      ) : (
        <Flex.Row
          alignItems={'center'}
          padding={{ horizontal: 12, vertical: 12 }}
        >
          {isNotNil(goodsDetail) ? (
            <LikeButton
              initialIsLiked={isNotNil(goodsDetail.goods.like)}
              likesCount={goodsDetail.goods.score.likes_count}
            />
          ) : (
            <LikeButton initialIsLiked={false} likesCount={0} />
          )}
          {isGiftPurchase && <GiftButton onPress={onPressGift} />}
          <Flex.Row flex={1} gap={8}>
            <Flex flex={1}>
              <PrimaryButton
                state={isDisabled ? 'disabled' : undefined}
                variant={'fill'}
                onPress={onPressPurchase}
                testID={'구매하기버튼'}
              >
                {buttonText}
              </PrimaryButton>
              {isOnSaleNow && (
                <Flex position={'absolute'} top={-6} right={8}>
                  <ImageBadgeFreeDelivery></ImageBadgeFreeDelivery>
                </Flex>
              )}
            </Flex>
            {!isOnSaleNow && goodsBasic?.is_possible_applying_restock_noti && (
              <Flex flex={1}>
                <PrimaryButton onPress={onPressRestockNoti} variant={'fill'}>
                  재입고 알림
                </PrimaryButton>
              </Flex>
            )}
          </Flex.Row>
        </Flex.Row>
      )}

      <SafeAreaBottomSpacing />
    </Flex>
  );
};
